"use client";

import { useCallback, useState } from "react";
import { useRouter } from "next/navigation";
import { useMutation } from "convex/react";
import { toast } from "sonner";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import type {
  BuyAgainReview,
  BuyAgainStageResult,
} from "@convex/schemas/validators/buyAgain.lib";
import { handleError } from "@/lib/handle-error";
import {
  OPEN_CHECKOUT_MESSAGES,
  useOpenCheckoutRelease,
} from "./use-open-checkout-release";
import { seedShipmentPlans } from "./use-shipment-plan";
import { track } from "@/lib/analytics/tracker";

interface BuyAgainConfirmation {
  signature: string;
  keepCartQuantityFor: Id<"products">[];
}

export interface BuyAgainTarget {
  orderId: Id<"orders">;
  productIds?: Id<"products">[];
}

export const BUY_AGAIN_MESSAGES = {
  ...OPEN_CHECKOUT_MESSAGES,
  changedElsewhere:
    "Checkout changed in another tab. Press Buy again.",
} as const;

function followOutcome(
  outcome: BuyAgainStageResult,
  goTo: (href: string) => void,
): BuyAgainReview | null {
  if (outcome.status === "staged") {
    seedShipmentPlans(outcome.ownerId, outcome.productIds, outcome.shipments);
    goTo("/cart");
  } else if (outcome.status === "checkout_open") {
    toast.info(BUY_AGAIN_MESSAGES.changedElsewhere);
  }
  return outcome.status === "review" ? outcome.review : null;
}

export function useBuyAgain({ orderId, productIds }: BuyAgainTarget) {
  const stage = useMutation(api.orders.buyAgain.stageBuyAgain);
  const openCheckout = useOpenCheckoutRelease();
  const router = useRouter();
  const [review, setReview] = useState<BuyAgainReview | null>(null);
  const [isWorking, setIsWorking] = useState(false);

  const run = useCallback(
    async (confirmation: BuyAgainConfirmation | undefined) => {
      setIsWorking(true);
      try {
        if (!(await openCheckout.releaseIfOpen())) return;
        const outcome = await stage({
          orderId,
          productIds,
          reviewedSignature: confirmation?.signature,
          keepCartQuantityFor: confirmation?.keepCartQuantityFor,
        });
        track("order.reorder_started", { source_order_id: orderId, method: "buy_again", outcome: outcome.status });
        setReview(followOutcome(outcome, router.push));
      } catch (thrown) {
        handleError(thrown, {
          display: "banner",
          fallbackMessage: "Couldn't start Buy again",
          reportIssueContext: { titlePrefix: "Buy again failed" },
        });
      } finally {
        setIsWorking(false);
      }
    },
    [openCheckout, orderId, productIds, router.push, stage],
  );

  return {
    review,
    isWorking,
    isLoading: openCheckout.isLoading,
    start: () => run(undefined),
    confirm: (keepCartQuantityFor: Id<"products">[]) =>
      review ? run({ signature: review.signature, keepCartQuantityFor }) : undefined,
    dismissReview: () => setReview(null),
  };
}
